import { useEffect } from 'react';

/**
 * Content Protection Hook
 * Prevents copying, selecting text, and right-clicking for non-admin users.
 *
 * IMPORTANT: This hook must NEVER interfere with:
 *   - Admin users (isAdmin === true)
 *   - Inline edit mode (body has the class `cs-editing`)
 *   - Any [contenteditable] target (needed for Safari caret placement)
 *   - Any element opting in via [data-edit-safe]
 */
export const useContentProtection = (isAdmin = false) => {
  useEffect(() => {
    // Skip protection for admins entirely
    if (isAdmin) return;

    // Helper: return true if the event target (or an ancestor) should be
    // exempt from protection. This is the single source of truth so text
    // selection, keyboard input and caret placement remain fully functional
    // wherever we allow editing.
    const isExempt = (target) => {
      if (!target || typeof target.closest !== 'function') return false;
      // Any active inline-edit session on the page
      if (document.body.classList.contains('cs-editing')) return true;
      // Editable surfaces (inputs, textareas, contenteditable)
      if (target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [contenteditable="plaintext-only"]')) return true;
      // Opt-out marker
      if (target.closest('[data-edit-safe]')) return true;
      return false;
    };

    // Disable right-click context menu
    const handleContextMenu = (e) => {
      if (isExempt(e.target)) return;
      e.preventDefault();
      return false;
    };

    // Disable copy
    const handleCopy = (e) => {
      if (isExempt(e.target)) return;
      e.preventDefault();
      return false;
    };

    // Disable cut
    const handleCut = (e) => {
      if (isExempt(e.target)) return;
      e.preventDefault();
      return false;
    };

    // Disable keyboard shortcuts (Ctrl+C, Ctrl+A, Ctrl+U, etc.)
    const handleKeyDown = (e) => {
      if (isExempt(e.target)) return;
      // Ctrl+C, Ctrl+X, Ctrl+A, Ctrl+U, Ctrl+S, Ctrl+P
      if (e.ctrlKey && ['c', 'x', 'a', 'u', 's', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      // Cmd+C, Cmd+X, Cmd+A, Cmd+U, Cmd+S, Cmd+P (Mac)
      if (e.metaKey && ['c', 'x', 'a', 'u', 's', 'p'].includes(e.key.toLowerCase())) {
        e.preventDefault();
        return false;
      }
      // F12 (Dev Tools)
      if (e.key === 'F12') {
        e.preventDefault();
        return false;
      }
    };

    // Disable text selection via selectstart event.
    // Safari relies on `selectstart` to place a caret inside contenteditable,
    // so we MUST allow it there or inline editing dies silently.
    const handleSelectStart = (e) => {
      if (isExempt(e.target)) return true;
      e.preventDefault();
      return false;
    };

    // Disable drag
    const handleDragStart = (e) => {
      if (isExempt(e.target)) return;
      e.preventDefault();
      return false;
    };

    // Add event listeners
    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('copy', handleCopy);
    document.addEventListener('cut', handleCut);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('dragstart', handleDragStart);

    // Cleanup
    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('copy', handleCopy);
      document.removeEventListener('cut', handleCut);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('dragstart', handleDragStart);
    };
  }, [isAdmin]);
};

/**
 * Content Protection CSS Styles
 * Add these to prevent text selection visually.
 *
 * The rules must NOT apply while inline edit mode is active, or Safari will
 * refuse to focus contenteditable spans. We scope everything with
 * `body:not(.cs-editing) .content-protected` and always allow selection on
 * editable surfaces.
 */
export const contentProtectionStyles = `
  /* Disable text selection for non-admin users (except while editing) */
  body:not(.cs-editing) .content-protected {
    -webkit-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    -webkit-touch-callout: none;
  }

  /* Always allow selection in form inputs and editable surfaces */
  .content-protected input,
  .content-protected textarea,
  .content-protected [contenteditable=""],
  .content-protected [contenteditable="true"],
  .content-protected [contenteditable="plaintext-only"],
  body.cs-editing .content-protected,
  body.cs-editing .content-protected * {
    -webkit-user-select: text !important;
    -moz-user-select: text !important;
    -ms-user-select: text !important;
    user-select: text !important;
    -webkit-touch-callout: default !important;
  }

  /* Disable image dragging for non-admins, but keep pointer events so
     the inline image editor overlay remains clickable while editing. */
  body:not(.cs-editing) .content-protected img {
    -webkit-user-drag: none;
    -khtml-user-drag: none;
    -moz-user-drag: none;
    -o-user-drag: none;
    user-drag: none;
  }
`;

export default useContentProtection;
